import { useId } from 'react';

import type { Copy, Lang } from './content-draft.ts';
import { FieldHead, InfoHint } from '~/components/InfoHint.tsx';
import { RichText } from '~/components/RichText.tsx';
import { useI18n } from '~/i18n/index.tsx';

/** Each language in its own name, so the switch reads the same from either side of the panel. */
const NATIVE: Record<Lang, string> = { ar: 'العربية', en: 'English' };

/**
 * Which language the content editors are writing in.
 *
 * One language at a time, where the catalogue's short names sit side by side. A product name is
 * five words; a policy section is five paragraphs, and two rich-text boxes at half width each
 * made every one of them harder to write. The cost of tabs — hiding the half you forgot — is paid
 * back on the switch itself: a language with gaps says how many, and each field missing in the
 * language on screen quotes what the other one says.
 */
export function WritingLanguage({
  value,
  onChange,
  gaps,
  end,
}: {
  value: Lang;
  onChange: (lang: Lang) => void;
  gaps: Record<Lang, number>;
  /** Anything that belongs at the far end of the bar — "view on the shop". */
  end?: React.ReactNode;
}) {
  const { t } = useI18n();

  return (
    <div className="writing-lang">
      <div className="writing-lang__group" role="group" aria-label={t('content.writingIn')}>
        <span className="writing-lang__label" aria-hidden="true">
          {t('content.writingIn')}
        </span>
        <div className="segmented">
          {(['ar', 'en'] as const).map((lang) => (
            <button
              key={lang}
              type="button"
              className={`segmented__item ${value === lang ? 'is-selected' : ''}`}
              aria-pressed={value === lang}
              onClick={() => onChange(lang)}
            >
              <span lang={lang}>{NATIVE[lang]}</span>
              {gaps[lang] > 0 ? (
                <span className="writing-lang__gap">{t('content.gaps', { count: gaps[lang] })}</span>
              ) : null}
            </button>
          ))}
        </div>
      </div>

      {end}
    </div>
  );
}

/**
 * What the other language says, under a field that is empty in this one.
 *
 * Only where that is the case: a full field needs no reference, and an empty pair is a choice.
 * Quoted rather than linked, because the point is to translate it here without switching away.
 */
function OtherLanguage({ copy, lang }: { copy: Copy; lang: Lang }) {
  const { t } = useI18n();
  const other: Lang = lang === 'ar' ? 'en' : 'ar';

  if (copy[lang].trim() !== '' || copy[other].trim() === '') {
    return null;
  }

  const text = copy[other].replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();

  return (
    <span className="copy-gap">
      {t('content.otherSays', { language: t(other === 'ar' ? 'lang.arabic' : 'lang.english') })}{' '}
      <q lang={other} dir={other === 'ar' ? 'rtl' : 'ltr'}>
        {text.length > 120 ? `${text.slice(0, 120)}…` : text}
      </q>
    </span>
  );
}

/** One piece of plain copy, in the language being written. */
export function CopyInput({
  label,
  hint,
  lang,
  value,
  onChange,
  multiline = false,
}: {
  label: string;
  hint?: string;
  lang: Lang;
  value: Copy;
  onChange: (next: Copy) => void;
  multiline?: boolean;
}) {
  const id = useId();
  const common = {
    id,
    lang,
    dir: lang === 'ar' ? 'rtl' : 'ltr',
    value: value[lang],
    'aria-describedby': hint ? `${id}-hint` : undefined,
  } as const;

  return (
    <div className="field">
      <FieldHead htmlFor={id} label={label} hint={hint} hintId={`${id}-hint`} />
      {multiline ? (
        <textarea
          {...common}
          className="awj-textarea"
          rows={2}
          onChange={(event) => onChange({ ...value, [lang]: event.target.value })}
        />
      ) : (
        <input
          {...common}
          className="awj-input"
          onChange={(event) => onChange({ ...value, [lang]: event.target.value })}
        />
      )}
      <OtherLanguage copy={value} lang={lang} />
    </div>
  );
}

/**
 * Long copy, in the language being written.
 *
 * Keyed on the language so switching gives each its own editor: one editor re-filled with the
 * other language's text would carry the first one's undo history and direction across.
 */
export function CopyRichText({
  label,
  lang,
  value,
  onChange,
}: {
  label: string;
  lang: Lang;
  value: Copy;
  onChange: (next: Copy) => void;
}) {
  return (
    <div className="field">
      {/* Not `.field__label`: the reference uses that name for a floating label it positions
          absolutely over the control. */}
      <span aria-hidden="true">{label}</span>
      <RichText
        key={lang}
        lang={lang}
        value={value[lang]}
        ariaLabel={label}
        onChange={(html) => onChange({ ...value, [lang]: html })}
      />
      <OtherLanguage copy={value} lang={lang} />
    </div>
  );
}

/** Whether a part or a section is on the shop. A setting, so a switch rather than a checkbox. */
export function VisibilitySwitch({
  checked,
  onChange,
  label,
  hint,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  hint?: string;
}) {
  const id = useId();

  return (
    // The whole row is the label, so a press anywhere on it flips the switch. `htmlFor` is not
    // optional here: a label without it belongs to the first labelable thing inside it, and the
    // "i" button comes first — every press on the row opened the note instead. The switch takes
    // its name from the words alone, or it would also be called "more information".
    <label className={`switch-field ${checked ? 'is-on' : ''}`} htmlFor={id}>
      <span className="switch-field__text">
        <span className="switch-field__label" id={`${id}-label`}>
          {label}
        </span>
        {hint ? <InfoHint text={hint} id={`${id}-hint`} /> : null}
      </span>
      <input
        id={id}
        className="switch-field__input"
        type="checkbox"
        role="switch"
        aria-labelledby={`${id}-label`}
        aria-describedby={hint ? `${id}-hint` : undefined}
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="switch-field__track" aria-hidden="true">
        <span className="switch-field__thumb" />
      </span>
    </label>
  );
}
